import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Stack,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  ArrowUpward,
  ArrowDownward,
  Save,
} from "@mui/icons-material";
import axios from "axios";
import config from "../../config";
import useLayoutStore from "../../store/useLayoutStore";

const drawerWidth = 240;
const collapsedWidth = 80;

const tableDenseSx = {
  "& .MuiTableCell-root": {
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: "0.86rem",
    lineHeight: 1.25,
    borderBottom: (t) => `1px solid ${t.palette.divider}`,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    paddingTop: 6,
    paddingBottom: 6,
    fontSize: "0.8rem",
    fontWeight: 700,
    background: (t) => (t.palette.mode === "dark" ? "#121212" : "#fafafa"),
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  "& .MuiIconButton-root": { padding: 6 },
  "& .MuiSvgIcon-root": { fontSize: "0.95rem" },
  "& .MuiCheckbox-root": { transform: "scale(0.9)" },
  "& ::-webkit-scrollbar": { width: 6, height: 6 },
  "& ::-webkit-scrollbar-thumb": {
    backgroundColor: "#bdbdbd",
    borderRadius: 8,
  },
  "& ::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  "&": { scrollbarWidth: "thin" },
};

const hideXs = { display: { xs: "none", sm: "table-cell" } };
const ellipsisCell = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const formatPrice = (v) => `${Number(v || 0).toLocaleString()}₫`;

// Tạo slug từ tiếng Việt
const toSlug = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export default function CollectionProducts() {
  const { isSidebarOpen } = useLayoutStore();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [searchAll, setSearchAll] = useState("");
  const [searchIn, setSearchIn] = useState("");
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingRight, setLoadingRight] = useState(false);
  const [error, setError] = useState("");

  const [allProducts, setAllProducts] = useState([]);
  const [inCollection, setInCollection] = useState([]);

  const [selectedLeftIds, setSelectedLeftIds] = useState(new Set());
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  // STATE: dialog tạo collection
  const [openCreate, setOpenCreate] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newEra, setNewEra] = useState("new"); // "new" | "old"
  const [newActive, setNewActive] = useState(true);
  const [newSortOrder, setNewSortOrder] = useState("");

  const showToast = useCallback(
    (msg, sev = "success") => setToast({ open: true, msg, sev }),
    []
  );

  // Load collections
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await axios.get(`${config.apiBaseUrl}/collections`, {
          params: { admin: true },
          signal: controller.signal,
        });
        const items = res?.data?.items || res?.data || [];
        setCollections(items);
        if (items.length && !collectionId)
          setCollectionId(items[0].id ?? items[0].collectionId);
      } catch (e) {
        if (!axios.isCancel(e))
          setError(
            e?.response?.data?.message || "Không tải được danh sách bộ sưu tập."
          );
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line
  }, []);

  // Auto tạo slug theo name nếu slug chưa gõ
  useEffect(() => {
    if (!newSlug) setNewSlug(toSlug(newName));
  }, [newName]); // eslint-disable-line

  // Load all products
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setLoadingLeft(true);
        const res = await axios.get(`${config.apiBaseUrl}/Products`, {
          signal: controller.signal,
        });
        const data = res?.data || [];
        const mapped = data.map((item) => ({
          id: item.productId,
          name: item.name,
          sku: item.sku,
          basePrice: item.price ?? item.basePrice ?? 0,
          priceText: formatPrice(item.price ?? item.basePrice ?? 0),
          totalStock: item.totalStock ?? 0,
          stockStatus: (item.totalStock ?? 0) > 0 ? "Còn hàng" : "Hết hàng",
        }));
        setAllProducts(mapped);
      } catch (e) {
        if (!axios.isCancel(e))
          setError(
            e?.response?.data?.message || "Không tải được danh sách sản phẩm."
          );
      } finally {
        setLoadingLeft(false);
      }
    })();
    return () => controller.abort();
  }, []);

  // Load products in collection
  useEffect(() => {
    if (!collectionId) return;
    const controller = new AbortController();
    (async () => {
      try {
        setLoadingRight(true);
        const res = await axios.get(
          `${config.apiBaseUrl}/collections/${collectionId}/products`,
          {
            params: { page: 1, pageSize: 999, sort: "order" },
            signal: controller.signal,
          }
        );
        const list = res?.data?.items || res?.data?.products || res?.data || [];
        const items = list.map((p, idx) => ({
          id: p.productId ?? p.id,
          name: p.name,
          sku: p.sku,
          basePrice: p.finalPrice ?? p.basePrice ?? 0,
          priceText: formatPrice(p.finalPrice ?? p.basePrice ?? 0),
          sortOrder: p.sortOrder ?? idx + 1,
          totalStock: p.totalStock ?? p.stock ?? 0,
          stockStatus:
            (p.totalStock ?? p.stock ?? 0) > 0 ? "Còn hàng" : "Hết hàng",
        }));
        items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setInCollection(items);
      } catch (e) {
        if (!axios.isCancel(e))
          setError(
            e?.response?.data?.message ||
              "Không tải được sản phẩm trong bộ sưu tập."
          );
      } finally {
        setLoadingRight(false);
      }
    })();
    return () => controller.abort();
  }, [collectionId]);

  // Filter
  const filteredAll = useMemo(() => {
    const q = (searchAll || "").toLowerCase();
    return allProducts.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)
    );
  }, [allProducts, searchAll]);

  const filteredIn = useMemo(() => {
    const q = (searchIn || "").toLowerCase();
    return inCollection.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)
    );
  }, [inCollection, searchIn]);

  // Multi-select (left)
  const toggleLeft = useCallback((id, checked) => {
    setSelectedLeftIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const toggleLeftAllFiltered = useCallback(
    (checked) => {
      setSelectedLeftIds((prev) => {
        const next = new Set(prev);
        filteredAll.forEach((r) => {
          checked ? next.add(r.id) : next.delete(r.id);
        });
        return next;
      });
    },
    [filteredAll]
  );

  const showToastMsg = (msg, sev = "success") => showToast(msg, sev);

  // Add 1 SP vào BST
  const handleAddOne = useCallback(
    async (row) => {
      if (!collectionId)
        return showToastMsg("Vui lòng chọn bộ sưu tập trước.", "warning");
      if (inCollection.some((x) => x.id === row.id))
        return showToastMsg("Sản phẩm đã có trong bộ sưu tập.", "info");
      try {
        const sortOrder =
          (inCollection[inCollection.length - 1]?.sortOrder ?? 0) + 1;
        await axios.post(
          `${config.apiBaseUrl}/collections/${collectionId}/products`,
          [{ productId: row.id, sortOrder }]
        );
        setInCollection((prev) => [
          ...prev,
          {
            id: row.id,
            name: row.name,
            sku: row.sku,
            basePrice: row.basePrice,
            priceText: row.priceText,
            sortOrder,
            totalStock: row.totalStock,
            stockStatus: row.stockStatus,
          },
        ]);
        showToastMsg("Đã thêm vào bộ sưu tập.");
      } catch (e) {
        showToastMsg(e?.response?.data?.message || "Thêm thất bại.", "error");
      }
    },
    [collectionId, inCollection]
  );
  const resetForm = () => {
    setNewName("");
    setNewSlug("");
    setNewDescription("");
    setNewEra("new");
    setNewActive(true);
    setNewSortOrder("");
  };

  // Add nhiều SP
  const handleAddMany = useCallback(async () => {
    if (!collectionId) return;
    const existing = new Set(inCollection.map((x) => x.id));
    const idsToAdd = [...selectedLeftIds].filter((id) => !existing.has(id));
    if (idsToAdd.length === 0)
      return showToastMsg("Tất cả sản phẩm đã có trong bộ sưu tập.", "info");
    try {
      const startOrder =
        (inCollection[inCollection.length - 1]?.sortOrder ?? 0) + 1;
      const payload = idsToAdd.map((id, i) => ({
        productId: id,
        sortOrder: startOrder + i,
      }));
      await axios.post(
        `${config.apiBaseUrl}/collections/${collectionId}/products`,
        payload
      );
      const mapById = new Map(allProducts.map((p) => [p.id, p]));
      setInCollection((prev) => [
        ...prev,
        ...payload.map((p, i) => {
          const src = mapById.get(p.productId);
          return {
            id: p.productId,
            name: src?.name ?? "",
            sku: src?.sku ?? "",
            basePrice: src?.basePrice ?? 0,
            priceText: src ? src.priceText : formatPrice(0),
            sortOrder: startOrder + i,
            totalStock: src?.totalStock ?? 0,
            stockStatus: src?.stockStatus ?? "Hết hàng",
          };
        }),
      ]);
      setSelectedLeftIds((prev) => {
        const next = new Set(prev);
        idsToAdd.forEach((id) => next.delete(id));
        return next;
      });
      showToastMsg(`Đã thêm ${idsToAdd.length} sản phẩm.`);
    } catch (e) {
      showToastMsg(e?.response?.data?.message || "Thêm thất bại.", "error");
    }
  }, [collectionId, inCollection, selectedLeftIds, allProducts]);

  const handleRemoveFromCollection = useCallback(
    async (row) => {
      try {
        await axios.delete(
          `${config.apiBaseUrl}/collections/${collectionId}/products/${row.id}`
        );
        setInCollection((prev) =>
          prev
            .filter((p) => p.id !== row.id)
            .map((x, i) => ({ ...x, sortOrder: i + 1 }))
        );
        showToastMsg("Đã xoá khỏi bộ sưu tập.");
      } catch (e) {
        showToastMsg(e?.response?.data?.message || "Xoá thất bại.", "error");
      }
    },
    [collectionId]
  );

  const moveItemById = useCallback((rowId, dir) => {
    setInCollection((prev) => {
      const list = [...prev];
      const index = list.findIndex((x) => x.id === rowId);
      if (index === -1) return prev;
      const newIdx = dir === "up" ? index - 1 : index + 1;
      if (newIdx < 0 || newIdx >= list.length) return prev;
      [list[index], list[newIdx]] = [list[newIdx], list[index]];
      return list.map((x, i) => ({ ...x, sortOrder: i + 1 }));
    });
  }, []);

  const saveReorder = useCallback(async () => {
    try {
      const orders = inCollection.map((x) => ({
        productId: x.id,
        sortOrder: x.sortOrder,
      }));
      await axios.patch(
        `${config.apiBaseUrl}/collections/${collectionId}/reorder`,
        orders
      );
      showToastMsg("Đã lưu sắp xếp.");
    } catch (e) {
      showToastMsg(
        e?.response?.data?.message || "Lưu sắp xếp thất bại.",
        "error"
      );
    }
  }, [collectionId, inCollection]);

  // Tạo collection mới
  const createCollection = async () => {
    if (!newName.trim()) return showToast("Tên không được để trống", "warning");
    const payload = {
      name: newName.trim(),
      slug: (newSlug || toSlug(newName)).trim(),
      description: newDescription?.trim() || null,
      isActive: Boolean(newActive),
      sortOrder:
        newSortOrder === "" || newSortOrder === null
          ? null
          : Number(newSortOrder),
      era: newEra, // "new" | "old"
    };
    try {
      setCreating(true);
      const res = await axios.post(`${config.apiBaseUrl}/collections`, payload);
      const newId = res?.data?.id;

      // Thêm nhanh vào list & chọn luôn
      const newItem = {
        id: newId,
        name: payload.name,
        slug: payload.slug,
        description: payload.description,
        isActive: payload.isActive,
        sortOrder: payload.sortOrder,
        era: payload.era,
      };
      setCollections((prev) => [newItem, ...prev]);
      if (newId) setCollectionId(newId);

      // reset form
      setOpenCreate(false);
      setNewName("");
      setNewSlug("");
      setNewDescription("");
      setNewEra("new");
      setNewActive(true);
      setNewSortOrder("");
      showToast("Đã tạo bộ sưu tập mới.");
    } catch (e) {
      showToast(
        e?.response?.data?.message || "Tạo bộ sưu tập thất bại.",
        "error"
      );
    } finally {
      setCreating(false);
    }
  };

  const saveCollection = async () => {
    if (!newName.trim()) return showToast("Tên không được để trống", "warning");

    const payload = {
      name: newName.trim(),
      slug: (newSlug || toSlug(newName)).trim(),
      description: newDescription?.trim() || null,
      isActive: Boolean(newActive),
      sortOrder: newSortOrder === "" ? null : Number(newSortOrder),
      era: newEra,
    };

    try {
      setCreating(true);
      if (editing) {
        // update
        await axios.put(
          `${config.apiBaseUrl}/collections/${editing.id}`,
          payload
        );
        setCollections((prev) =>
          prev.map((c) =>
            (c.id ?? c.collectionId) === editing.id ? { ...c, ...payload } : c
          )
        );
        showToast("Đã cập nhật bộ sưu tập.");
      } else {
        // create
        const res = await axios.post(
          `${config.apiBaseUrl}/collections`,
          payload
        );
        const newId = res?.data?.id;
        const newItem = { id: newId, ...payload };
        setCollections((prev) => [newItem, ...prev]);
        if (newId) setCollectionId(newId);
        showToast("Đã tạo bộ sưu tập mới.");
      }
      setOpenForm(false);
      setEditing(null);
      resetForm();
    } catch (e) {
      showToast(e?.response?.data?.message || "Lưu thất bại.", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCollection = async () => {
    if (!collectionId) return;
    if (!window.confirm("Bạn có chắc chắn muốn xoá bộ sưu tập này?")) return;
    try {
      await axios.delete(`${config.apiBaseUrl}/collections/${collectionId}`);
      setCollections((prev) =>
        prev.filter((c) => (c.id ?? c.collectionId) !== collectionId)
      );
      setCollectionId("");
      showToast("Đã xoá bộ sưu tập.");
    } catch (e) {
      showToast(e?.response?.data?.message || "Xoá thất bại.", "error");
    }
  };

  // Chiều cao tối đa bảng theo viewport
  const TABLE_H = {
    xs: 360,
    sm: 460,
    md: "calc(100vh - 240px)",
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 1.5, md: 2 },
        width: `calc(100vw - ${
          isSidebarOpen ? drawerWidth : collapsedWidth
        }px)`,
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s",
        minHeight: 0,
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <Typography variant={smDown ? "subtitle1" : "h6"} fontWeight="bold">
          Sản phẩm trong bộ sưu tập
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <TextField
            select
            size="small"
            label="Chọn bộ sưu tập"
            value={collectionId}
            onChange={(e) => {
              setCollectionId(e.target.value);
              setSelectedLeftIds(new Set());
            }}
            sx={{
              minWidth: { xs: "100%", sm: 260 },
              width: { xs: "100%", sm: 300 },
            }}
            fullWidth={smDown}
          >
            {collections.map((c) => (
              <MenuItem
                key={c.id ?? c.collectionId}
                value={c.id ?? c.collectionId}
              >
                {c.name} {c.era ? `(${c.era})` : ""}
              </MenuItem>
            ))}
          </TextField>

          {/* Nút tạo BST */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={() => setOpenCreate(true)}
          >
            Tạo bộ sưu tập
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            disabled={!collectionId}
            onClick={() => {
              const col = collections.find(
                (c) => (c.id ?? c.collectionId) === collectionId
              );
              if (col) {
                setEditing(col);
                setNewName(col.name);
                setNewSlug(col.slug);
                setNewDescription(col.description ?? "");
                setNewEra(col.era ?? "new");
                setNewActive(Boolean(col.isActive));
                setNewSortOrder(col.sortOrder ?? "");
                setOpenForm(true);
              }
            }}
          >
            Sửa
          </Button>

          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<Delete />}
            disabled={!collectionId}
            onClick={handleDeleteCollection}
          >
            Xoá
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            disabled={!collectionId || selectedLeftIds.size === 0}
            onClick={handleAddMany}
          >
            {smDown
              ? `Thêm (${selectedLeftIds.size || 0})`
              : `Thêm ${selectedLeftIds.size || ""} sản phẩm`}
          </Button>
        </Stack>
      </Stack>

      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main */}
      <Grid
        container
        spacing={{ xs: 1.5, md: 2 }}
        sx={{ flex: 1, minHeight: 0 }}
      >
        {/* LEFT */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Tất cả sản phẩm
            </Typography>
            <TextField
              value={searchAll}
              onChange={(e) => setSearchAll(e.target.value)}
              size="small"
              placeholder="Tìm theo tên hoặc SKU..."
              sx={{ width: { xs: "100%", sm: 280, md: 340 } }}
              fullWidth={smDown}
            />
          </Stack>

          {loadingLeft ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAll.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              Không có sản phẩm phù hợp.
            </Box>
          ) : (
            <TableContainer
              component={Box}
              sx={{
                width: "100%",
                flex: "1 1 auto",
                minHeight: 0,
                maxHeight: { xs: TABLE_H.xs, sm: TABLE_H.sm, md: TABLE_H.md },
                overflow: "auto",
                ...tableDenseSx,
              }}
            >
              <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }}>
                      <Checkbox
                        size="small"
                        indeterminate={
                          filteredAll.length > 0 &&
                          filteredAll.some((r) => selectedLeftIds.has(r.id)) &&
                          !filteredAll.every((r) => selectedLeftIds.has(r.id))
                        }
                        checked={
                          filteredAll.length > 0 &&
                          filteredAll.every((r) => selectedLeftIds.has(r.id))
                        }
                        onChange={(e) =>
                          toggleLeftAllFiltered(e.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ ...hideXs, width: { sm: 72 } }}>
                      Mã SP
                    </TableCell>
                    <TableCell>Tên sản phẩm</TableCell>
                    <TableCell sx={{ ...hideXs, width: { sm: 100 } }}>
                      Tồn kho
                    </TableCell>
                    <TableCell align="right" sx={{ width: 56 }}>
                      Thêm
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAll.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={selectedLeftIds.has(row.id)}
                          onChange={(e) => toggleLeft(row.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={hideXs}>{row.id}</TableCell>
                      <TableCell sx={ellipsisCell} title={row.name}>
                        {row.name}
                      </TableCell>
                      <TableCell sx={hideXs}>
                        {row.totalStock} ({row.stockStatus})
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Thêm vào bộ sưu tập">
                          <IconButton
                            color="primary"
                            onClick={() => handleAddOne(row)}
                            size="small"
                          >
                            <Add />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>

        {/* RIGHT */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Trong bộ sưu tập {collectionId ? `#${collectionId}` : ""}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <TextField
                value={searchIn}
                onChange={(e) => setSearchIn(e.target.value)}
                size="small"
                placeholder="Tìm trong bộ sưu tập..."
                sx={{ width: { xs: "100%", sm: 220 } }}
                fullWidth={smDown}
              />
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={saveReorder}
                disabled={!collectionId || inCollection.length === 0}
              >
                {smDown ? "Lưu" : "Lưu sắp xếp"}
              </Button>
            </Stack>
          </Stack>

          <TableContainer
            component={Box}
            sx={{
              width: "100%",
              flex: "1 1 auto",
              minHeight: 0,
              maxHeight: { xs: TABLE_H.xs, sm: TABLE_H.sm, md: TABLE_H.md },
              overflow: "auto",
              ...tableDenseSx,
            }}
          >
            <Table
              stickyHeader
              size="small"
              aria-label="Trong bộ sưu tập"
              sx={{ tableLayout: "fixed" }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 44 }}>#</TableCell>
                  <TableCell sx={{ ...hideXs, width: { sm: 72 } }}>
                    Mã SP
                  </TableCell>
                  <TableCell>Tên sản phẩm</TableCell>
                  <TableCell sx={{ width: { xs: 110, sm: 120 } }}>
                    Thao tác
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIn.map((row) => {
                  const realIdx = inCollection.findIndex(
                    (x) => x.id === row.id
                  );
                  const isFirst = realIdx <= 0;
                  const isLast = realIdx === inCollection.length - 1;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.sortOrder}</TableCell>
                      <TableCell sx={hideXs}>{row.id}</TableCell>
                      <TableCell sx={ellipsisCell} title={row.name}>
                        {row.name}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Di chuyển lên">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveItemById(row.id, "up")}
                                disabled={isFirst}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Di chuyển xuống">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveItemById(row.id, "down")}
                                disabled={isLast}
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Xoá khỏi bộ sưu tập">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRemoveFromCollection(row)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2200}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.sev}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Dialog tạo bộ sưu tập */}
      <Dialog
        open={openCreate}
        onClose={() => !creating && setOpenCreate(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Tạo bộ sưu tập</DialogTitle>
        <DialogContent
          sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            autoFocus
            label="Tên"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="VD: Bộ sưu tập mới 08/2025"
            size="small"
            fullWidth
          />
          <TextField
            label="Slug"
            value={newSlug}
            onChange={(e) => setNewSlug(toSlug(e.target.value))}
            placeholder="vd: new-drop-2025-08"
            helperText="Tự tạo từ tên, có thể chỉnh"
            size="small"
            fullWidth
          />
          <TextField
            label="Mô tả"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            select
            label="Era"
            size="small"
            value={newEra}
            onChange={(e) => setNewEra(e.target.value)}
          >
            <MenuItem value="all">all</MenuItem>
            <MenuItem value="new">new</MenuItem>
            <MenuItem value="old">old</MenuItem>
          </TextField>
          <TextField
            type="number"
            label="Sort order"
            size="small"
            value={newSortOrder}
            onChange={(e) => setNewSortOrder(e.target.value)}
            placeholder="VD: 1"
            inputProps={{ min: 0, step: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
            }
            label="Kích hoạt (IsActive)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)} disabled={creating}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            onClick={createCollection}
            disabled={creating}
          >
            {creating ? "Đang tạo..." : "Tạo"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openForm}
        onClose={() => !creating && setOpenForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing ? "Sửa bộ sưu tập" : "Tạo bộ sưu tập"}
        </DialogTitle>
        <DialogContent
          sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Tên"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            label="Slug"
            value={newSlug}
            onChange={(e) => setNewSlug(toSlug(e.target.value))}
          />
          <TextField
            label="Mô tả"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            multiline
          />
          <TextField
            select
            label="Era"
            value={newEra}
            onChange={(e) => setNewEra(e.target.value)}
          >
            <MenuItem value="new">new</MenuItem>
            <MenuItem value="old">old</MenuItem>
          </TextField>
          <TextField
            type="number"
            label="Sort order"
            value={newSortOrder}
            onChange={(e) => setNewSortOrder(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
            }
            label="Kích hoạt"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Huỷ</Button>
          <Button
            variant="contained"
            onClick={saveCollection}
            disabled={creating}
          >
            {creating ? "Đang lưu..." : editing ? "Lưu" : "Tạo"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
